import { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Globe, LogOut, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useShopper } from "@/components/customer/ShopperProvider";
import MemberDialog from "@/components/customer/MemberDialog";
import { COUNTRIES } from "@/lib/api";
import Img from "@/components/Img";
import { useLang, useT } from "@/lib/i18n";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <button data-testid="site-lang-toggle" onClick={() => setLang(lang === "id" ? "en" : "id")}
      className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-bold uppercase text-slate-700 hover:bg-slate-100">{lang === "id" ? "EN" : "ID"}</button>
  );
};

export const BrandMark = () => {
  const { site } = useShopper();
  return (
  <Link to="/" data-testid="brand-logo" className="flex min-w-0 items-center gap-1.5 font-heading text-xl font-bold tracking-tight">
    {site?.logo ? <Img src={site.logo} size="sm" eager alt={site.site_name} className="h-9 w-auto max-w-[180px] object-contain" /> : <>
    <span className="h-2.5 w-2.5 shrink-0 rotate-45 bg-[#B4693E]" />
    <span className="truncate text-base text-[#1C1917] sm:text-xl">{site?.site_name || "Bedsolving Connect"}</span></>}
  </Link>
  );
};

const MemberButton = () => {
  const { member, signOut } = useShopper();
  const t = useT();
  const [open, setOpen] = useState(false);
  if (member) return (
    <div className="flex items-center gap-1">
      <span data-testid="member-badge" className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-amber-800">
        <Crown className="h-4 w-4 text-[#FFB700]" /> <span className="hidden max-w-[120px] truncate sm:inline">{member.name}</span>
      </span>
      <button data-testid="member-logout" onClick={signOut} title="Keluar" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><LogOut className="h-4 w-4" /></button>
    </div>
  );
  return (
    <>
      <button data-testid="member-login-button" onClick={() => setOpen(true)} className="flex items-center gap-1.5 rounded-lg bg-[#5392F9] px-3 py-2 text-white transition-colors hover:bg-[#3B7BE6]">
        <Crown className="h-4 w-4" /> <span className="hidden sm:inline">{t("Masuk Member")}</span>
      </button>
      <MemberDialog open={open} onOpenChange={setOpen} />
    </>
  );
};

export default function CustomerHeader() {
  const { country, setCountry } = useShopper();
  const t = useT();
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:px-6">
        <BrandMark />
        <nav className="flex shrink-0 items-center gap-0.5 text-sm font-medium sm:gap-2">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid="country-select" className="h-9 w-auto gap-1.5 border-0 bg-transparent px-2 shadow-none hover:bg-slate-100">
              <Globe className="h-4 w-4 text-slate-500" /><span className="hidden sm:inline"><SelectValue /></span>
            </SelectTrigger>
            <SelectContent>{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c} data-testid={`country-option-${c}`}>{c} · {n}</SelectItem>)}</SelectContent>
          </Select>
          <Link to="/find-booking" data-testid="nav-find-booking" className="flex items-center gap-1.5 rounded-lg px-2 py-2 text-slate-700 transition-colors hover:bg-slate-100 hover:text-[#5392F9] sm:px-3">
            <Search className="h-4 w-4" /> <span className="hidden md:inline">{t("Cek Pesanan")}</span>
          </Link>
          <LangSwitch />
          <MemberButton />
        </nav>
      </div>
    </header>
  );
}

export const CustomerFooter = () => {
  const { site } = useShopper();
  const { lang } = useLang();
  return (
  <footer className="mt-20 border-t border-[#E7E0D6] bg-[#1C1917] text-stone-300">
    <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-10 text-sm sm:flex-row sm:justify-between">
      <span className="font-heading text-xl font-bold text-white">{site?.site_name}</span>
      <div className="space-y-1 sm:text-right" data-testid="footer-contact">
        {site?.contact_email && <p>{site.contact_email}</p>}
        {site?.contact_phone && <p>{site.contact_phone}</p>}
        {site?.whatsapp && <a className="text-[#D9A066] hover:underline" href={`https://wa.me/${site.whatsapp.replace(/\D/g, "")}`}>WhatsApp {site.whatsapp}</a>}
        <p className="text-stone-500">{(lang === "en" && site?.footer_text_en) || site?.footer_text}</p>
      </div>
    </div>
  </footer>
  );
};
